/**
 * NewVersionWatcher — offers the new build instead of reloading on the person
 * (REVIEW-2). Mounted once by the shell.
 *
 * - When a page's code is gone because a new build is live, `lazyWithRetry`
 *   announces it; this shows one carbon toast with a Refresh button and keeps
 *   the person exactly where they are.
 * - It also checks quietly when the tab comes back into view (at most every five
 *   minutes), so the offer usually arrives before any page has to say it.
 * - It never reloads by itself.
 */
import { useEffect, useRef } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { NEW_VERSION_EVENT, announceNewVersion, isNewVersionLive } from '../../utils/appVersion';

const CHECK_EVERY_MS = 5 * 60 * 1000;
const OFFER_MS = 10 * 60 * 1000;

export default function NewVersionWatcher(): null {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    const offer = () => {
      toastRef.current.showToast('A new version of the app is ready.', 'info', {
        action: { label: 'Refresh', onClick: () => window.location.reload() },
        duration: OFFER_MS,
      });
    };
    window.addEventListener(NEW_VERSION_EVENT, offer);

    let lastCheck = Date.now();
    const check = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastCheck < CHECK_EVERY_MS) return;
      lastCheck = Date.now();
      void isNewVersionLive().then((live) => { if (live) announceNewVersion(); });
    };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.removeEventListener(NEW_VERSION_EVENT, offer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  return null;
}
